// F4 (plano híbrido §2.1/§3.3.1, D-H1) — coexistência legado × novo.
//
// Cada radio dos modais de upload mapeia UMA combinação { action, flow }:
// - flow 'legacy' = pipeline da develop (data-import) — payload do import
//   IDÊNTICO ao da develop (o campo flow é omitido; o Joi do sync-backend
//   aplica o default 'legacy' na escrita);
// - flow 'v2' = pipeline novo (api-performance) — o payload leva flow: 'v2'.
//
// A disponibilidade de cada fluxo é gated por tenant via GET /sync/settings
// (D-H13): opções "(Novo)" só aparecem com v2ImportEnabled=true; as legadas
// só com legacyImportEnabled=true. Em erro na busca das flags o default é
// conservador (legado visível, novo oculto).
import {
  PEOPLE_ACTIONS,
  GROUPS_ACTIONS,
  OCCUPATIONS_ACTIONS,
} from '@/helpers/upload-actions'
import { getSyncSettings } from '@/services/settings'

const SYNC_FLOWS = {
  Legacy: 'legacy',
  V2: 'v2',
}

// Espelha SYNC_FLAGS_DEFAULTS do sync-backend (plano §9.1):
// legacyImportEnabled default true, v2ImportEnabled default false.
const DEFAULT_SYNC_FLAGS = {
  legacyImportEnabled: true,
  v2ImportEnabled: false,
}

const buildOption = (flow, action, labelKey) => ({
  id: `${flow}:${action}`,
  flow,
  action,
  labelKey,
})

// Ordem de exibição: legadas primeiro (como na develop), "(Novo)" depois.
const PEOPLE_UPLOAD_OPTIONS = [
  buildOption(
    SYNC_FLOWS.Legacy,
    PEOPLE_ACTIONS.AddUpdate,
    'PeopleService.Modal.labels.action.addUpdate'
  ),
  buildOption(
    SYNC_FLOWS.Legacy,
    PEOPLE_ACTIONS.Delete,
    'PeopleService.Modal.labels.action.delete'
  ),
  buildOption(
    SYNC_FLOWS.V2,
    PEOPLE_ACTIONS.AddUpdate,
    'PeopleService.Modal.labels.action.addUpdateV2'
  ),
  buildOption(
    SYNC_FLOWS.V2,
    PEOPLE_ACTIONS.Delete,
    'PeopleService.Modal.labels.action.deleteV2'
  ),
]

// Matriz action×flow (§2.1): groups delete NÃO existe no legado — a opção
// Delete só entra no bloco v2.
const GROUPS_UPLOAD_OPTIONS = [
  buildOption(
    SYNC_FLOWS.Legacy,
    GROUPS_ACTIONS.FullHierarchyAddUpdate,
    'GroupsService.Modal.labels.action.FullHierarchyAddUpdate'
  ),
  buildOption(
    SYNC_FLOWS.Legacy,
    GROUPS_ACTIONS.PartialAddUpdate,
    'GroupsService.Modal.labels.action.PartialAddUpdate'
  ),
  buildOption(
    SYNC_FLOWS.Legacy,
    GROUPS_ACTIONS.InformationUpdate,
    'GroupsService.Modal.labels.action.InformationUpdate'
  ),
  buildOption(
    SYNC_FLOWS.V2,
    GROUPS_ACTIONS.FullHierarchyAddUpdate,
    'GroupsService.Modal.labels.action.FullHierarchyAddUpdateV2'
  ),
  buildOption(
    SYNC_FLOWS.V2,
    GROUPS_ACTIONS.PartialAddUpdate,
    'GroupsService.Modal.labels.action.PartialAddUpdateV2'
  ),
  buildOption(
    SYNC_FLOWS.V2,
    GROUPS_ACTIONS.InformationUpdate,
    'GroupsService.Modal.labels.action.InformationUpdateV2'
  ),
  buildOption(
    SYNC_FLOWS.V2,
    GROUPS_ACTIONS.Delete,
    'GroupsService.Modal.labels.action.DeleteV2'
  ),
]

const normalizeSyncFlags = flags => ({
  legacyImportEnabled:
    flags?.legacyImportEnabled ?? DEFAULT_SYNC_FLAGS.legacyImportEnabled,
  v2ImportEnabled: flags?.v2ImportEnabled ?? DEFAULT_SYNC_FLAGS.v2ImportEnabled,
})

const filterUploadOptionsByFlags = (options, flags) => {
  const { legacyImportEnabled, v2ImportEnabled } = normalizeSyncFlags(flags)

  return options.filter(option =>
    option.flow === SYNC_FLOWS.Legacy ? legacyImportEnabled : v2ImportEnabled
  )
}

const findUploadOption = (options, id) =>
  options.find(option => option.id === id)

// ── Rótulo da opção de importação nas listagens (item 5) ────────────────────
// A coluna "Ação" das listagens de arquivos exibe o MESMO texto do radio do
// modal: resolve { action, flow } do processed file na opção correspondente.
// Arquivo sem flow (pré-híbrido) é legado — default de leitura D-H1.
// Combinação desconhecida retorna null (a listagem exibe '--', não quebra).
const findUploadOptionLabelKey = (options, { action, flow }) =>
  findUploadOption(options, `${flow || SYNC_FLOWS.Legacy}:${action}`)
    ?.labelKey || null

// Occupations não tem flow — mapa direto action → labelKey do radio do modal
const OCCUPATIONS_ACTION_LABEL_KEYS = {
  [OCCUPATIONS_ACTIONS.AddUpdate]:
    'OccupationsService.Modal.labels.action.addUpdate',
  [OCCUPATIONS_ACTIONS.Delete]: 'OccupationsService.Modal.labels.action.delete',
}

// Consome GET /sync/settings (F2). Falha NUNCA quebra o modal: cai para o
// default conservador (v2 oculto — plano §3.3.1 "default oculto em erro").
const resolveSyncFlags = async () => {
  try {
    const { data } = await getSyncSettings()
    return normalizeSyncFlags(data)
  } catch (error) {
    return { ...DEFAULT_SYNC_FLAGS }
  }
}

export {
  SYNC_FLOWS,
  DEFAULT_SYNC_FLAGS,
  PEOPLE_UPLOAD_OPTIONS,
  GROUPS_UPLOAD_OPTIONS,
  OCCUPATIONS_ACTION_LABEL_KEYS,
  normalizeSyncFlags,
  filterUploadOptionsByFlags,
  findUploadOption,
  findUploadOptionLabelKey,
  resolveSyncFlags,
}
